import { env } from '@/lib/env';

import type { components } from './schema';

type ErrorCode = components['schemas']['Error']['error']['code'];

/** Error del envelope `{ error: { code, message, details } }` (catálogo en la API: docs/error-codes.md). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const SESSION_ENDED = new Set([
  'SESSION_REVOKED',
  'SESSION_EXPIRED',
  'REFRESH_INVALID',
  'TOKEN_INVALID',
  'UNAUTHORIZED',
]);

export function mensajeDe(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'No pudimos conectar con Consalud. Revisa tu conexión e inténtalo de nuevo.';
}

/** URL del legacy si el error es `BETA_NOT_INVITED` (beta por invitación, plan §17.1). */
export function legacyDeBeta(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.code !== 'BETA_NOT_INVITED') return null;
  const url = err.details.legacyUrl;
  return typeof url === 'string' && url.startsWith('https://') ? url : env.legacyUrl;
}

/* -------------------------------------------------------- cuenta suspendida o bloqueada */

/**
 * 403 al crear o renovar una sesión (login, 2FA, refresh…) cuando la cuenta tiene una suspensión
 * o un bloqueo vigente. La API solo lo responde a quien ya demostró ser el dueño de la cuenta.
 * Contrato: `AccountDeniedError`, `AccountSuspendedDetails` y `AccountBlockedDetails` del OpenAPI.
 */
export type CodigoRestriccion = Extract<
  ErrorCode,
  components['schemas']['AccountDeniedError']['error']['code']
>;

export type DatosTransferencia = NonNullable<
  components['schemas']['AccountSuspendedDetails']['payment']
>;

/** `SESSION_EXPIRED` con estos motivos: el token vivo dejó de valer por una suspensión o bloqueo. */
export const MOTIVOS_RESTRICCION: ReadonlySet<unknown> = new Set([
  'admin_suspended',
  'admin_blocked',
]);

export type CuentaRestringida =
  | {
      code: 'ACCOUNT_SUSPENDED';
      message: string;
      reason: string | null;
      /** Decimal en string ("1500.00"). */
      amountDue: string | null;
      /** Fecha de negocio "YYYY-MM-DD". */
      dueOn: string | null;
      payment: DatosTransferencia | null;
    }
  | { code: 'ACCOUNT_BLOCKED'; message: string; reason: string | null };

const CODIGOS_RESTRICCION: ReadonlySet<string> = new Set<CodigoRestriccion>([
  'ACCOUNT_SUSPENDED',
  'ACCOUNT_BLOCKED',
]);

const texto = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : null;

function transferencia(v: unknown): DatosTransferencia | null {
  if (!v || typeof v !== 'object') return null;
  const p = v as Record<string, unknown>;
  const clabe = texto(p.clabe);
  if (!clabe) return null;
  return { bank: texto(p.bank) ?? '', clabe, holder: texto(p.holder) ?? '', email: texto(p.email) };
}

/** Lee `ACCOUNT_SUSPENDED` / `ACCOUNT_BLOCKED` (403) de un error; `null` si es otro error. */
export function cuentaRestringidaDe(err: unknown): CuentaRestringida | null {
  if (!(err instanceof ApiError) || err.status !== 403 || !CODIGOS_RESTRICCION.has(err.code)) {
    return null;
  }
  const d = err.details;
  if (err.code === 'ACCOUNT_BLOCKED') {
    return { code: 'ACCOUNT_BLOCKED', message: err.message, reason: texto(d.reason) };
  }
  return {
    code: 'ACCOUNT_SUSPENDED',
    message: err.message,
    reason: texto(d.reason),
    amountDue: texto(d.amountDue),
    dueOn: texto(d.dueOn),
    payment: transferencia(d.payment),
  };
}
